import React from "react";
import type { WarmupPhase } from "../hooks/useWarmup";
import { WARMUP_MESSAGES } from "./WarmupMessages";

// Keyframes only — inline styles handle everything else
const KEYFRAMES = `
  @keyframes vw-pulse {
    0%, 100% { opacity: 1; }
    50%       { opacity: 0.72; }
  }
  @keyframes vw-shimmer {
    0%   { transform: translateX(-100%); }
    100% { transform: translateX(350%); }
  }
  @keyframes vw-d1 { 0%,20%{ opacity:0 } 30%,80%{ opacity:1 } 100%{ opacity:0 } }
  @keyframes vw-d2 { 0%,40%{ opacity:0 } 50%,90%{ opacity:1 } 100%{ opacity:0 } }
  @keyframes vw-d3 { 0%,60%{ opacity:0 } 70%,100%{ opacity:1 } }
`;

function AnimatedDots() {
  const s = (anim: string): React.CSSProperties => ({
    animation: anim,
    opacity: 0,
    color: "#6b7280",
  });
  return (
    <>
      <span style={s("vw-d1 1.6s infinite")}>.</span>
      <span style={s("vw-d2 1.6s infinite")}>.</span>
      <span style={{ animation: "vw-d3 1.6s infinite", color: "#6b7280" }}>.</span>
    </>
  );
}

export default function Warmup({ phase }: { phase: WarmupPhase }) {
  const [msgIdx, setMsgIdx] = React.useState(() =>
    Math.floor(Math.random() * WARMUP_MESSAGES.length)
  );
  const [msgVisible, setMsgVisible] = React.useState(true);

  React.useEffect(() => {
    if (phase !== "loading") return;
    const timer = setInterval(() => {
      setMsgVisible(false);
      setTimeout(() => {
        setMsgIdx((prev) => {
          // True random pick — avoid showing the same message twice in a row
          let next: number;
          do {
            next = Math.floor(Math.random() * WARMUP_MESSAGES.length);
          } while (next === prev && WARMUP_MESSAGES.length > 1);
          return next;
        });
        setMsgVisible(true);
      }, 500);
    }, 11000); // 11s gives enough time to read longer sayings
    return () => clearInterval(timer);
  }, [phase]);

  const msg = WARMUP_MESSAGES[msgIdx];

  // ── Root: full-screen overlay, pure flex centering ────────────────────────
  const rootStyle: React.CSSProperties = {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: "#f9fafb",
    zIndex: 99999,
    fontFamily: "'Rubik', sans-serif",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 24,
    opacity: phase === "fading" ? 0 : 1,
    transition: "opacity 600ms ease-out",
  };

  // ── Logo: explicit pixel dimensions — cannot shift anything ───────────────
  const logoWrapStyle: React.CSSProperties = {
    width: 72,
    height: 62,           // 72 × (95 / 111.5) = 61.4 — matches SVG aspect ratio
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    animation: "vw-pulse 2.8s ease-in-out infinite",
  };

  // ── Message: fixed height so text changes never shift the logo ────────────
  const msgWrapStyle: React.CSSProperties = {
    width: 460,
    maxWidth: "88vw",
    height: 60,           // tall enough for 2 lines at 15.5px / 1.6 lh
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
  };

  const msgStyle: React.CSSProperties = {
    color: "#111827",
    fontSize: 15.5,
    fontWeight: 400,
    letterSpacing: "0.01em",
    textAlign: "center",
    lineHeight: 1.6,
    opacity: msgVisible ? 1 : 0,
    transition: "opacity 0.4s ease-in-out",
  };

  // ── Progress bar ──────────────────────────────────────────────────────────
  const barTrackStyle: React.CSSProperties = {
    width: 140,
    height: 2,
    background: "#e5e7eb",
    borderRadius: 2,
    overflow: "hidden",
    flexShrink: 0,
  };

  const barFillStyle: React.CSSProperties = {
    width: "45%",
    height: "100%",
    background: "linear-gradient(90deg, #4C75AB 0%, #2FBF71 100%)",
    borderRadius: 2,
    animation: "vw-shimmer 1.6s ease-in-out infinite",
  };

  return (
    <>
      <style>{KEYFRAMES}</style>
      <div style={rootStyle}>
        {/* Logo — fixed-size, no influence on surrounding layout */}
        <div style={logoWrapStyle}>
          <svg
            viewBox="0 0 111.5 95"
            width={72}
            height={62}
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fill="#2FBF71"
              d="M48.1,91.7C-5.2,54.5,18,30.4,54.1,29C70.5,28.3,71,1.3,53.5,3.5C36.2,5.7-1,20.2,5.8,46.7C11.1,66.8,35.7,87.2,48.1,91.7z"
            />
            <path
              fill="#4C75AB"
              d="M63.4,3.3c53.3,37.1,30.1,61.2-6,62.7c-16.3,0.7-16.8,27.7,0.6,25.5c17.2-2.2,54.5-16.8,47.7-43.2C100.5,28.1,75.8,7.8,63.4,3.3z"
            />
            <circle cx="55.8" cy="47.5" r="6.5" fill="#4C75AB" />
          </svg>
        </div>

        {/* Message — fixed 60px container, zero layout reflow */}
        <div style={msgWrapStyle}>
          <span style={msgStyle}>
            {msg.text}
            {msg.dots && <AnimatedDots />}
          </span>
        </div>

        {/* Progress bar */}
        <div style={barTrackStyle}>
          <div style={barFillStyle} />
        </div>
      </div>
    </>
  );
}
